<template>
  <AppLayout>
    <div class="users-page">
      <div class="page-header">
        <h1>Utilisateurs</h1>
        <BaseButton variant="primary" @click="showCreateModal = true">
          + Nouvel utilisateur
        </BaseButton>
      </div>

      <div v-if="error" class="error">{{ error }}</div>
      <BaseTable
        :columns="columns"
        :items="users"
        :loading="loading"
        :show-actions="true"
        @view="viewUser"
      >
        <template #cell-role="{ value }">
          <BaseBadge :variant="getRoleVariant(value)">{{ value }}</BaseBadge>
        </template>
        <template #actions="{ item }">
          <BaseButton variant="ghost" size="sm" @click="viewUser(item)">
            Voir
          </BaseButton>
          <BaseButton variant="ghost" size="sm" @click.stop="editUser(item)">
            Modifier
          </BaseButton>
          <BaseButton 
            variant="ghost" 
            size="sm" 
            @click.stop="confirmDelete(item)" 
            :disabled="item.id === currentUserId"
          >
            Supprimer
          </BaseButton>
        </template>
      </BaseTable>

      <div v-if="!loading && users.length > 0" class="pagination">
        <BaseButton variant="ghost" @click="previousPage" :disabled="offset === 0">
          ← Précédent
        </BaseButton>
        <span class="pagination-info">
          Page {{ currentPage }} sur {{ totalPages }} ({{ total }} utilisateurs)
        </span>
        <BaseButton variant="ghost" @click="nextPage" :disabled="offset + limit >= total">
          Suivant →
        </BaseButton>
      </div>
    </div>

    <!-- Modal de création/édition -->
    <FormPage v-if="showCreateModal || showEditModal" @close="closeModal">
      <template #title>
        {{ showCreateModal ? 'Nouvel utilisateur' : 'Modifier utilisateur' }}
      </template>
      <form @submit.prevent="handleSubmit" class="user-form">
        <BaseInput
          v-model.trim="userForm.nom"
          label="Nom"
          placeholder="Nom complet"
          required
        />
        <BaseInput
          v-model.trim="userForm.email"
          label="Email"
          type="email"
          placeholder="email@example.com"
          required
        />
        <BaseSelect
          v-model="userForm.role"
          label="Rôle"
          required
        >
          <option value="ADMIN">ADMIN</option>
          <option value="BUREAU">BUREAU</option>
          <option value="USINE">USINE</option>
          <option value="CHANTIER">CHANTIER</option>
          <option value="SUPERVISION">SUPERVISION</option>
        </BaseSelect>
        <BaseInput
          v-if="showCreateModal"
          v-model.trim="userForm.password"
          label="Mot de passe"
          type="password"
          placeholder="••••••••"
          required
        />
        <div v-if="formError" class="error-message">{{ formError }}</div>
        <div class="form-actions">
          <BaseButton type="button" variant="ghost" @click="closeModal">Annuler</BaseButton>
          <BaseButton type="submit" variant="primary" :loading="submitting">
            {{ showCreateModal ? 'Créer' : 'Enregistrer' }}
          </BaseButton>
        </div>
      </form>
    </FormPage>

    <!-- Modal de confirmation de suppression -->
    <FormPage v-if="showDeleteModal" @close="showDeleteModal = false">
      <template #title>Confirmer la suppression</template>
      <p>Êtes-vous sûr de vouloir supprimer l'utilisateur <strong>{{ userToDelete?.nom }}</strong> ?</p>
      <div class="form-actions">
        <BaseButton type="button" variant="ghost" @click="showDeleteModal = false">Annuler</BaseButton>
        <BaseButton variant="primary" @click="handleDelete" :loading="deleting">
          Supprimer
        </BaseButton>
      </div>
    </FormPage>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import AppLayout from '../components/AppLayout.vue';
import BaseButton from '../components/BaseButton.vue';
import BaseInput from '../components/BaseInput.vue';
import BaseSelect from '../components/BaseSelect.vue';
import BaseBadge from '../components/BaseBadge.vue';
import BaseTable, { type TableColumn } from '../components/BaseTable.vue';
import FormPage from '../components/FormPage.vue';
import { apiFetch } from '../utils/api';
import { useAuth } from '../composables/useAuth';

interface User {
  id: number;
  nom: string;
  email: string;
  role: string;
  creeLe: string;
}

const router = useRouter();
const { user: currentUser } = useAuth();
const currentUserId = computed(() => currentUser.value?.id);

const users = ref<User[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const limit = ref(20);
const offset = ref(0);
const total = ref(0);

const showCreateModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const submitting = ref(false);
const deleting = ref(false);
const formError = ref<string | null>(null);
const userToDelete = ref<User | null>(null);
const editingUserId = ref<number | null>(null);

const userForm = reactive({
  nom: '',
  email: '',
  role: 'SUPERVISION',
  password: ''
});

const columns: TableColumn[] = [
  { key: 'nom', label: 'Nom' },
  { key: 'email', label: 'Email' },
  { key: 'role', label: 'Rôle' },
  { 
    key: 'creeLe', 
    label: 'Créé le',
    formatter: (value: string) => new Date(value).toLocaleDateString('fr-FR')
  }
];

const currentPage = computed(() => Math.floor(offset.value / limit.value) + 1);
const totalPages = computed(() => Math.ceil(total.value / limit.value));

onMounted(() => {
  loadUsers();
});

async function loadUsers() {
  loading.value = true;
  error.value = null;
  try {
    const response = await apiFetch(`/authentification/users?limit=${limit.value}&offset=${offset.value}`);
    const data = response as { items: User[]; total: number; limit: number; offset: number };
    users.value = data.items || [];
    total.value = data.total || 0;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement';
  } finally {
    loading.value = false;
  }
}

function viewUser(user: User) {
  router.push({ name: 'user-detail', params: { id: user.id } });
}

function editUser(user: User) {
  editingUserId.value = user.id;
  userForm.nom = user.nom;
  userForm.email = user.email;
  userForm.role = user.role;
  userForm.password = '';
  showEditModal.value = true;
}

function confirmDelete(user: User) {
  userToDelete.value = user;
  showDeleteModal.value = true;
}

function closeModal() {
  showCreateModal.value = false;
  showEditModal.value = false;
  formError.value = null;
  userForm.nom = '';
  userForm.email = '';
  userForm.role = 'SUPERVISION';
  userForm.password = '';
  editingUserId.value = null;
}

async function handleSubmit() {
  formError.value = null;
  submitting.value = true;

  try {
    if (showCreateModal.value) {
      await apiFetch('/authentification/register', {
        method: 'POST',
        body: {
          nom: userForm.nom,
          email: userForm.email,
          role: userForm.role,
          password: userForm.password
        }
      });
    } else if (editingUserId.value) {
      await apiFetch(`/authentification/users/${editingUserId.value}`, {
        method: 'PUT',
        body: {
          nom: userForm.nom,
          email: userForm.email,
          role: userForm.role
        }
      });
    }
    closeModal();
    loadUsers();
  } catch (err) {
    formError.value = err instanceof Error ? err.message : 'Erreur lors de l\'enregistrement';
  } finally {
    submitting.value = false;
  }
}

async function handleDelete() {
  if (!userToDelete.value) return;

  deleting.value = true;
  try {
    await apiFetch(`/authentification/users/${userToDelete.value.id}`, {
      method: 'DELETE'
    });
    showDeleteModal.value = false;
    userToDelete.value = null;
    loadUsers();
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors de la suppression';
  } finally {
    deleting.value = false;
  }
}

function nextPage() {
  if (offset.value + limit.value < total.value) {
    offset.value += limit.value;
    loadUsers();
  }
}

function previousPage() {
  if (offset.value > 0) {
    offset.value = Math.max(0, offset.value - limit.value);
    loadUsers();
  }
}

function getRoleVariant(role: string): 'default' | 'primary' | 'success' | 'warning' | 'error' {
  switch (role) {
    case 'ADMIN':
      return 'error';
    case 'BUREAU':
      return 'primary';
    case 'USINE':
      return 'success';
    default:
      return 'default';
  }
}
</script>

<style scoped>
.users-page {
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-lg);
  gap: var(--spacing-md);
}

.page-header h1 {
  margin: 0;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-md);
  margin-top: var(--spacing-xl);
}

.pagination-info {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.user-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
}

.error-message {
  padding: var(--spacing-sm);
  background: #fee;
  border: 1px solid #fcc;
  border-radius: var(--radius-md);
  color: #c33;
  font-size: var(--font-size-sm);
}

.error {
  padding: var(--spacing-md);
  background: #fee;
  border: 1px solid #fcc;
  border-radius: var(--radius-md);
  color: #c33;
  margin-bottom: var(--spacing-lg);
}
</style>
